/**
 * Additional Dizzaroo workbook checks: Literature References, Tables/Figures captions,
 * Appendices, Headers/Footers/Pagination hints, Margins/Paper size (document-declared).
 */

const limitErrors = (errors, max = 12) => {
  const list = Array.isArray(errors) ? errors : [];
  if (list.length <= max) return list;
  return [...list.slice(0, max), `…and ${list.length - max} more issue(s).`];
};

const getCombinedText = (flat) => flat.map((s) => s.text).filter(Boolean).join('\n\n');
const getCombinedHtml = (flat) => flat.map((s) => s.html).filter(Boolean).join('\n');

const findSection = (flat, predicate) => flat.find((s) => predicate(s.title || '', s.text || ''));

/** References section present and numerically ordered */
export const checkReferencesSectionPresent = (flat) => {
  // Also accepts an "Appendix" prefix ("14. APPENDIX: REFERENCES", "Appendix 3 – References"),
  // the form ICH M11 / USDM-generated protocols use for the reference list.
  const ref = findSection(
    flat,
    (title) =>
      /^(\d+\.?\s*)?(appendix\s*[\w.]*\s*[:\-–—]?\s*)?(references|literature|bibliography)\b/i.test(title.trim()),
  );
  if (!ref) {
    return {
      passed: false,
      errors: ['No "References" / literature section found at end of document structure.'],
    };
  }
  const text = ref.text || '';
  const numbered = text.match(/(?:^|\n)\s*\[\d+\]|(?:^|\n)\s*\d+\.\s+[A-Z]/g) || [];
  // An HTML ordered list is numbered when rendered/exported, but its numbers never
  // appear in the stripped text the regex above sees.
  const htmlOrderedList = /<ol[\s>][\s\S]*?<li[\s>]/i.test(ref.html || '');
  if (numbered.length === 0 && !htmlOrderedList && text.length > 80) {
    return {
      passed: false,
      errors: [
        'References section found but entries do not appear numerically numbered (AMA: order of first appearance).',
      ],
    };
  }
  return { passed: true, errors: [] };
};

/** Bracket-style citations [1] preferred detection — flag if roman or shared ambiguity */
export const checkInTextCitationStyle = (flat) => {
  const errors = [];
  const html = getCombinedHtml(flat);
  const text = getCombinedText(flat);

  // Standalone only: "[L]" inside "anti-PD-[L]1" is part of a drug-class name.
  if (/(?<![\w-])\[[ivxlcdm]+\](?![\w-])/i.test(text)) {
    errors.push('In-text citations appear to use Roman numerals in brackets — use superscript Arabic numerals.');
  }
  // Many protocols use [1] in HTML; also flag bare (1) citation clusters as possible non-superscript
  const bracketCites = text.match(/\[\d{1,3}\]/g) || [];
  const hasSup = /<sup[^>]*>\s*\d+\s*<\/sup>/i.test(html);
  if (bracketCites.length > 5 && !hasSup) {
    errors.push(
      `Found ${bracketCites.length} bracket citations like [n] without <sup> markup — style guide prefers superscript Arabic numerals.`,
    );
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Personal communications / unpublished should not be in numbered reference list */
export const checkPersonalCommunicationsInRefs = (flat) => {
  const errors = [];
  const ref = findSection(flat, (title) => /references|bibliography|literature/i.test(title));
  if (!ref) return { passed: true, errors: [] };
  const text = ref.text || '';
  if (/personal communication/i.test(text)) {
    errors.push('Personal communications must not appear in the numbered reference list — cite parenthetically in text only.');
  }
  if (/unpublished (manuscript|data|observation)/i.test(text)) {
    errors.push('Unpublished manuscripts/data must not appear in the numbered reference list.');
  }
  return { passed: errors.length === 0, errors };
};

/** Active hyperlinks to external URLs in references */
export const checkReferenceExternalHyperlinks = (flat) => {
  const errors = [];
  const ref = findSection(flat, (title) => /references|bibliography|literature/i.test(title));
  if (!ref?.html) return { passed: true, errors: [] };
  const linkRegex = /<a[^>]+href=["'](https?:\/\/[^"']+)["'][^>]*>/gi;
  let m;
  while ((m = linkRegex.exec(ref.html)) !== null) {
    // Internal anchors ok; external http(s) flagged
    errors.push(`External hyperlink in References not permitted — display URL as plain text (found ${m[1]}).`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Minimum reference elements heuristic */
export const checkReferenceMinimumElements = (flat) => {
  const errors = [];
  const ref = findSection(flat, (title) => /references|bibliography|literature/i.test(title));
  if (!ref) return { passed: true, errors: [] };
  const text = ref.text || '';
  const entries = text
    .split(/(?:^|\n)\s*(?:\[\d+\]|\d+\.)\s+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 40);
  entries.slice(0, 25).forEach((entry, idx) => {
    const hasYear = /\b(19|20)\d{2}\b/.test(entry);
    const hasUrlOrJournal = /https?:\/\/|doi\.org|\.gov|\.org|journal|Lancet|JAMA|N Engl|Clin\b/i.test(entry);
    if (!hasYear) errors.push(`Reference entry ${idx + 1} may be missing a date/year.`);
    if (!hasUrlOrJournal && entry.length > 60) {
      errors.push(`Reference entry ${idx + 1} may be missing source (journal/book/URL).`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** DOI format https://doi.org/... without trailing period */
export const checkDoiFormat = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  if (/\bdoi:\s*10\./i.test(text)) {
    errors.push('Use DOI in URL form (https://doi.org/...) — not "doi:" prefix.');
  }
  // Whole DOI token, flagged only when it ENDS with a period. DOIs contain periods
  // ("10.1200/JCO.2017.75.6155"); the old [^\s]+\. backtracked to any inner one.
  const doiToken = /https:\/\/doi\.org\/\S+/gi;
  let m;
  while ((m = doiToken.exec(text)) !== null) {
    if (m[0].endsWith('.')) errors.push(`No period after DOI/URL (found "${m[0]}").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Table/Figure captions use section-based Arabic numbering Table 1-1 */
export const checkCaptionNumberingFormat = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const captions = [];
  // Section-dash (1-1) and decimal (1.1) forms must be tried before the bare
  // digit[+letter] alternative, or "Table 1-1" matches as just "1" (alternation
  // stops at the first successful branch) and never counts toward dashForm below.
  const re = /\b(Table|Figure)\s+([IVXLCDM]+|\d+-\d+|\d+\.\d+|\d+[A-Za-z]?)\b/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    captions.push({ kind: m[1], num: m[2], raw: m[0] });
  }
  captions.forEach((c) => {
    if (/^[IVXLCDM]+$/i.test(c.num)) {
      errors.push(`Use Arabic section-based numbering, not Roman numerals (${c.raw}).`);
    } else if (/^\d+[A-Za-z]$/.test(c.num)) {
      errors.push(`Avoid lettered table/figure numbers (${c.raw}) — use Table/Figure <Section>-<N>.`);
    } else if (/^\d+$/.test(c.num)) {
      // Acceptable shorthand but style guide prefers Section-N; soft warning only if many
    } else if (!/^\d+-\d+$/.test(c.num) && !/^\d+\.\d+$/.test(c.num)) {
      errors.push(`Unexpected caption numbering format: "${c.raw}". Prefer Table 1-1 / Figure 1-1.`);
    }
  });
  // Prefer at least some section-dash form if many captions
  const dashForm = captions.filter((c) => /^\d+-\d+$/.test(c.num)).length;
  if (captions.length >= 3 && dashForm === 0) {
    errors.push(
      'Table/figure captions should use section-based format (e.g., Table 1-1, Figure 1-1) per style guide.',
    );
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** List of Tables / List of Figures for longer docs */
export const checkLotLofPresent = (flat) => {
  const textLen = getCombinedText(flat).length;
  // Heuristic: long protocol (~5+ pages) ≈ > 12000 chars of body
  if (textLen < 12000) return { passed: true, errors: [] };
  const titles = flat.map((s) => s.title || '').join('\n');
  const hasLot = /list of tables/i.test(titles);
  const hasLof = /list of figures/i.test(titles);
  const hasTables = /\bTable\s+\d/i.test(getCombinedText(flat));
  const hasFigures = /\bFigure\s+\d/i.test(getCombinedText(flat));
  if ((!hasTables || hasLot) && (!hasFigures || hasLof)) return { passed: true, errors: [] };
  // LOT/LOF are front matter the Word export builds from the table/figure captions
  // (DocumentEditor.jsx, inserted after the TOC) — they are never editor sections,
  // so their absence here is expected, not a defect. The PDF export does not add them.
  const built = [hasTables && !hasLot && 'List of Tables', hasFigures && !hasLof && 'List of Figures']
    .filter(Boolean)
    .join(' and ');
  return {
    passed: true,
    errors: [],
    successDetail: `${built} generated automatically in the Word export from the table/figure captions (not included in the PDF export).`,
  };
};

/** Footnote markers should be lowercase letters not *, †, ‡ */
export const checkFootnoteMarkerStyle = (flat) => {
  const errors = [];
  const html = getCombinedHtml(flat);
  const text = getCombinedText(flat);
  if (/<sup>\s*[\*†‡§]\s*<\/sup>/.test(html) || /[\*†‡§]\s*[A-Z]/.test(text)) {
    errors.push('Use sequential superscript lowercase letters (a, b, c) for footnotes — not *, †, ‡, §.');
  }
  return { passed: errors.length === 0, errors };
};

/** Appendix section present / sequential numbering hints */
export const checkAppendixNumbering = (flat) => {
  const errors = [];
  const appendixTitles = flat
    .map((s) => s.title || '')
    .filter((t) => /appendix/i.test(t));
  if (appendixTitles.length === 0) {
    return { passed: true, errors: [], successDetail: 'No appendices detected.' };
  }
  const nums = appendixTitles
    .map((t) => {
      const m = t.match(/appendix\s*([A-Z]|\d+(?:\.\d+)*)/i);
      return m ? m[1] : null;
    })
    .filter(Boolean);
  // Flag lettered appendices when M11 numeric expected in some orgs — soft
  const lettered = nums.filter((n) => /^[A-Z]$/i.test(n));
  if (lettered.length && nums.some((n) => /^\d/.test(n))) {
    errors.push('Mixed appendix numbering styles (letters and numbers) detected — keep one sequential scheme.');
  }
  return { passed: errors.length === 0, errors };
};

/** Confidential label present somewhere (footer expectation) */
export const checkConfidentialLabel = (flat) => {
  const text = getCombinedText(flat);
  const titles = flat.map((s) => s.title || '').join(' ');
  // Running headers/footers often not in section HTML — check for explicit Confidential mention
  // Soft pass with note if absent from body
  if (/\bconfidential\b/i.test(text) || /\bconfidential\b/i.test(titles)) {
    return { passed: true, errors: [] };
  }
  // The label normally comes from the export footer template, not section body
  // content, so its absence here isn't a real per-document defect — skip rather
  // than fail, matching the comment above (this used to hard-fail every document).
  return {
    passed: true,
    skipped: true,
    errors: [
      'No "Confidential" mention found in section content — verify the export footer template includes Confidential and page X of Y.',
    ],
  };
};

/** Pagination X of Y — usually export-time; flag if wrong patterns in text */
export const checkPaginationPatternInText = (flat) => {
  const text = getCombinedText(flat);
  const errors = [];
  if (/\bPage\s+\d+\s*$/im.test(text) && !/\bPage\s+\d+\s+of\s+\d+/i.test(text)) {
    errors.push('Pagination should use "Page X of Y" (or "X of Y"), not page number alone.');
  }
  return { passed: errors.length === 0, errors };
};

/** Agency vs paper size declared in text */
export const checkPaperSizeAgencyHint = (flat) => {
  const text = getCombinedText(flat);
  const errors = [];
  const mentionsFda = /\bFDA\b/.test(text) || /\bUnited States\b/.test(text);
  const mentionsEma = /\bEMA\b/.test(text) || /\bEuropean Medicines Agency\b/.test(text);
  const mentionsLetter = /US Letter|8\.5\s*[×x]\s*11/i.test(text);
  const mentionsA4 = /\bA4\b/.test(text);
  if (mentionsFda && mentionsA4 && !mentionsLetter) {
    errors.push('Document mentions FDA with A4 sizing language — FDA submissions typically use US Letter.');
  }
  if (mentionsEma && mentionsLetter && !mentionsA4) {
    errors.push('Document mentions EMA with US Letter sizing language — EMA/ICH typically use A4.');
  }
  // If nothing declared, pass
  return { passed: errors.length === 0, errors };
};

/** TOC present for longer documents */
export const checkTocPresent = (flat) => {
  const textLen = getCombinedText(flat).length;
  if (textLen < 12000) return { passed: true, errors: [] };
  const hasToc = flat.some((s) => /table of contents|^toc$/i.test((s.title || '').trim()));
  if (hasToc) return { passed: true, errors: [] };
  // The TOC is front matter both protocol exports build from the section headings
  // (Word: native TOC field with C-TOC styles; PDF: nativeProtocolPdfToc.js) — it is
  // never an editor section, so its absence here is expected, not a defect.
  return {
    passed: true,
    errors: [],
    successDetail: 'Table of Contents generated automatically at export from the section headings (Word: updatable TOC field; PDF: linked, page-numbered TOC).',
  };
};

/** Blue hyperlink color in figures/body (HTML color heuristics) */
export const checkBlueTextInContent = (flat) => {
  const errors = [];
  const html = getCombinedHtml(flat);
  if (/color\s*:\s*(#0000ff|blue|#00f\b)/i.test(html)) {
    errors.push('Blue-colored text detected — reserve blue for hyperlinks; avoid blue in figure/body text.');
  }
  return { passed: errors.length === 0, errors };
};

/** Tables/figures cited before placement (order heuristic) */
export const checkElementsCitedBeforePlacement = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const placements = [];
  // A caption is the full label followed by ":" (or "."/a dash then a space). The
  // lookahead stops "Table 3-1 summarizes…" from backtracking to "Table 3" + "-" and
  // being read as a caption for a different table.
  // A caption is the label followed by ":" or a dash — not a full stop, which is how
  // an in-text citation ends a sentence ("…are detailed in Table 6-1. Table 6-1 …").
  const placeRe = /\b(Table|Figure)\s+(\d+(?:[.-]\d+)*)(?![.-]?\d)\s*(?::|[\-–—](?=\s))/gi;
  let m;
  while ((m = placeRe.exec(text)) !== null) {
    placements.push({ kind: m[1].toLowerCase(), num: m[2], index: m.index, raw: m[0] });
  }
  placements.slice(0, 40).forEach((p) => {
    const before = text.slice(0, p.index);
    const citeRe = new RegExp(`\\b${p.kind}\\s+${p.num.replace(/[.-]/g, '\\$&')}(?![.-]?\\d)`, 'i');
    // First occurrence should ideally be a citation; if caption-like pattern is the first, soft flag
    const first = before.search(citeRe);
    if (first < 0) {
      errors.push(
        `${p.kind[0].toUpperCase()}${p.kind.slice(1)} ${p.num} appears as a caption/placement before any earlier in-text citation.`,
      );
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

export const LAYOUT_REF_RULE_CHECKS = [
  {
    id: 'ai-ref-section-present',
    label: 'Literature: References Section Present & Numbered',
    category: 'Literature References',
    run: checkReferencesSectionPresent,
  },
  {
    id: 'ai-ref-citation-style',
    label: 'Literature: In-Text Citation Style (Superscript Arabic)',
    category: 'Literature References',
    run: checkInTextCitationStyle,
  },
  {
    id: 'ai-ref-personal-comms',
    label: 'Literature: No Personal Communications in Reference List',
    category: 'Literature References',
    run: checkPersonalCommunicationsInRefs,
  },
  {
    id: 'ai-ref-external-links',
    label: 'Literature: No External Hyperlinks in References',
    category: 'Literature References',
    run: checkReferenceExternalHyperlinks,
  },
  {
    id: 'ai-ref-min-elements',
    label: 'Literature: Reference Minimum Elements',
    category: 'Literature References',
    run: checkReferenceMinimumElements,
  },
  {
    id: 'ai-ref-doi-format',
    label: 'Literature: DOI URL Format',
    category: 'Literature References',
    run: checkDoiFormat,
  },
  {
    id: 'ai-table-caption-format',
    label: 'Tables & Figures: Caption Numbering Format',
    category: 'Tables & Figures',
    run: checkCaptionNumberingFormat,
  },
  {
    id: 'ai-lot-lof',
    label: 'Tables & Figures: List of Tables/Figures Present',
    category: 'Tables & Figures',
    run: checkLotLofPresent,
  },
  {
    id: 'ai-footnote-markers',
    label: 'Tables & Figures: Footnote Letter Markers',
    category: 'Tables & Figures',
    run: checkFootnoteMarkerStyle,
  },
  {
    id: 'ai-appendix-numbering',
    label: 'Appendices: Numbering Consistency',
    category: 'Appendices',
    run: checkAppendixNumbering,
  },
  {
    id: 'ai-footer-confidential',
    label: 'Headers/Footers: Confidential Label Present',
    category: 'Cross-References & Navigation',
    run: checkConfidentialLabel,
  },
  {
    id: 'ai-pagination-x-of-y',
    label: 'Headers/Footers: Pagination X of Y Pattern',
    category: 'Cross-References & Navigation',
    run: checkPaginationPatternInText,
  },
  {
    id: 'ai-paper-size-agency',
    label: 'Margins & Layout: Paper Size vs Agency Hint',
    category: 'Margins & Page Layout',
    run: checkPaperSizeAgencyHint,
  },
  {
    id: 'ai-toc-present',
    label: 'Formatting: Table of Contents for Long Documents',
    category: 'Formatting & Style',
    run: checkTocPresent,
  },
  {
    id: 'ai-blue-text',
    label: 'Tables & Figures: Avoid Blue Body/Figure Text',
    category: 'Tables & Figures',
    run: checkBlueTextInContent,
  },
  {
    id: 'ai-cite-before-placement',
    label: 'Cross-References: Cite Tables/Figures Before Placement',
    category: 'Cross-References & Navigation',
    run: checkElementsCitedBeforePlacement,
  },
];
